import { Component, OnDestroy, OnInit } from "@angular/core"

import { Title } from "@angular/platform-browser"
import { ActivatedRoute, Params, Router } from "@angular/router"
import { CHNode } from "../models/node.model"
import { PBSLM_TITLE } from "../models/constants"
import { BrowsingService } from "../services/browsing.service"
import { ColorCodeService } from "../services/color-code.service"
import { TruncatePipe } from "../shared-components/pipes/truncate.pipe"
import { redirectToError } from "../utils/error-handling"
import { Location } from "@angular/common"
import { EduComponent } from "../utils/eduComponent"
import { Observable, Subject } from "rxjs"
import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout"
import { AddOnViewService } from "../services/add-on-view.service"
import { filter, takeUntil, tap } from "rxjs/operators"

@Component({
    selector: "app-browsing",
    templateUrl: "./browsing.component.html",
    styleUrls: ["./browsing.component.scss"],
    standalone: false,
})
export class BrowsingComponent extends EduComponent implements OnInit, OnDestroy {
    nodeDepth: number = 0
    resultsPerRow: number = 3
    bannerBg: string = ""
    activeNode: CHNode
    colorCode: string = "#fff"
    availableTopics: CHNode[] = []
    availableSubTopics: CHNode[] = []
    selectedTopic: CHNode | null = null
    selectedSubTopic: CHNode | null = null
    isTablet$: Observable<BreakpointState>
    isAddOnView: boolean

    private destroyed$ = new Subject()

    constructor(
        private browsingService: BrowsingService,
        private route: ActivatedRoute,
        private router: Router,
        private colorCodeService: ColorCodeService,
        private titleService: Title,
        private truncatePipe: TruncatePipe,
        private location: Location,
        private breakpointObserver: BreakpointObserver,
        private addOnViewService: AddOnViewService
    ) {
        super()
    }

    ngOnInit() {
        this.titleService.setTitle(PBSLM_TITLE)
        this.colorCode = this.colorCodeService.getPrimaryColor()
        this.isTablet$ = this.breakpointObserver.observe("(max-width: 991px)")
        this.isAddOnView = this.addOnViewService.isAddonView
        this.subscribeToRouteParams()
        this.subscribeToInvalidNodeSelected()
        this.subscribeToActiveNodeChanges()
    }

    public subscribeToInvalidNodeSelected(): void {
        this.browsingService.invalidNodeSelected.pipe(takeUntil(this.destroyed$)).subscribe(() => {
            redirectToError(this.router, this.location, "404")
        })
    }

    public subscribeToRouteParams(): void {
        this.route.params.pipe(takeUntil(this.destroyed$)).subscribe((params: Params) => {
            this.browsingService.selectNode(params.nodeSlug)
        })
    }

    public subscribeToActiveNodeChanges(): void {
        this.browsingService.activeNodeChanged
            .pipe(
                takeUntil(this.destroyed$),
                filter((node: CHNode) => this.hasNodeLoaded(node)),
                tap((node: CHNode) => this.setAvailableTopics(node)),
                tap((node: CHNode) => this.setSelectedTopics(node)),
                tap((node: CHNode) => this.setComponentState(node))
            )
            .subscribe()
    }

    public setAvailableTopics(node: CHNode): void {
        this.availableTopics = node.getSubject()?.children || []
        this.availableSubTopics = node.getTopic()?.children || []
    }

    public setSelectedTopics(node: CHNode): void {
        this.selectedTopic = node.getTopic() || null
        this.selectedSubTopic = node.getSubTopic() || null
    }

    public hasNodeLoaded(node: CHNode): boolean {
        return node.isLoaded()
    }

    public setComponentState(node: CHNode): void {
        this.browsingService.breadcrumbs = []
        this.bannerBg = node.banner
        this.activeNode = node
        this.colorCode = this.colorCodeService.getColorForNode(node)
        if (this.activeNode.title) {
            const title = this.truncatePipe.transform(this.activeNode.title, 250)
            const metaTitle = this.truncatePipe.transform(this.activeNode.metaTitle, 200)
            this.titleService.setTitle(metaTitle || title)
        }
        this.nodeDepth = node.getDepth()
        this.resultsPerRow = this.nodeDepth > 1 ? 3 : 4
    }

    public onNodeChange(node: CHNode): void {
        if (!node) return
        this.router.navigate([node.getBrowseURL()], { queryParamsHandling: "merge" })
    }

    ngOnDestroy() {
        super.ngOnDestroy()
        this.destroyed$.next()
        this.destroyed$.complete()
    }
}
